const GoogleCloudTranslate = require('@google-cloud/translate');
const PMSpecs = require('@techchange/pm-specs');
const TCRequest = require('@techchange/request');
const cld = require('cld');
const DeeplNode = require('deepl-node');
const ProseMirrorModel = require('prosemirror-model');

const { Translate } = GoogleCloudTranslate.v2;
const {
	deserialize,
	serialize: serializeProsemirror,
	sanitize: sanitizeProsemirror,
	schemaSpec,
} = PMSpecs;
const { Translator } = DeeplNode;
const { Schema } = ProseMirrorModel;

const {
	ASSOCIATED_OBJECT_TYPES,
	LANGUAGE_FALLBACK,
	LANGUAGE_UNDETERMINED,
	SCOPES,
	TRANSLATION_PROVIDERS
} = require('../constants');
const {
	DEEPL_SOURCE_LANGUAGES,
	DEEPL_TARGET_LANGUAGES,
} = require('../deepLLanguages');
const LANGUAGE_CODES = require('../languageCodes');
const { mockRequest, timeout } = require('../utils');

const schema = new Schema(schemaSpec);

const TIMEOUT_IN_MS = 3000;

class TCIntl {

	/*
	 * Initialize a new instance of the TCIntl class.
	 *
	 * @param {object}  settings  The settings.
	 * @param {object}  settings.deepl.apiKey  DeepL authentication settings.
	 * @param {object}  settings.gcloud  Google Cloud authentication settings.
	 * @param {object}  settings.internalNodeHeaders
	 *                  The authentication header key/value for internal API requests.
	 * @param {boolean} settings.testMode  True if translation requests should be mocked.
	 * @param {boolean} settings.timer  True if translation request times should be logged.
	 */
	constructor(settings = {}) {

		if (!settings.internalNodeHeaders) {
			throw new Error('Missing the "settings.internalNodeHeaders" parameter for TCIntl.');
		}

		if (!settings.deepl?.apiKey) {
			throw new Error('Missing the "settings.deepl" parameter for TCIntl.');
		}

		if (!settings.gcloud) {
			throw new Error('Missing the "settings.gcloud" parameter for TCIntl.');
		}

		this.tcRequest = new TCRequest({ authHeader: settings.internalNodeHeaders });
		this.deepLTranslator = new Translator(settings.deepl.apiKey);
		this.googleTranslate = new Translate({ ...settings.gcloud });

		this.testMode = Boolean(settings.testMode);
		this.timer = Boolean(settings.timer);

	}

	/**
	 * Determines the correct language(s) to use for a given request based on the scope.
	 *
	 * @param {object} params                           The request parameters.
	 * @param {string} params.hostname                  The request hostname.
	 * @param {string} params.scope                     The scope.
	 * @param {string} params.scope_id                  The scope ID.
	 * @param {string} params.organization_id           The organization ID.
	 * @param {string} [params.language]                The requested language.
	 * @param {string} [params.associated_object_type]  The associated object type.
	 * @param {string} [params.associated_object_id]    The associated object ID.
	 *
	 * @returns {object} result
	 * @returns {string} result.defaultLanguage  The default language to use.
	 * @returns {array}  result.languages        An array of language(s) to filter by.
	 */
	async parseRequestLanguage(params = {}) {

		const hostname = params.hostname;

		const requestedLanguage = params.language;

		const associatedObjectType = params.associated_object_type;
		const associatedObjectId = params.associated_object_id;

		const scope = params.scope;
		const scopeId = params.scope_id;
		const organizationId = params.organization_id;

		if (!organizationId) {
			throw new Error('Missing value for "params.organization_id".');
		}

		let defaultLanguage;

		switch (scope) {

		case SCOPES.SESSION:

			if (!scopeId) {
				throw new Error('Missing value for "params.scope_id".');
			}

			const [product] = await this.tcRequest.getAll(
				hostname,
				'products',
				{
					params: {
						session_id: scopeId,
						organization_id: organizationId,
					},
				},
			);

			defaultLanguage = product?.default_language || LANGUAGE_FALLBACK;

			break;

		case SCOPES.COURSE:
		case SCOPES.TRACK:

			if (!scopeId) {
				throw new Error('Missing value for "params.scope_id".');
			}

			// Fetch default language from the product
			const [productForItem] = await this.tcRequest.getAll(
				hostname,
				'products',
				{
					params: {
						type: scope,
						item_id: scopeId,
						organization_id: organizationId,
					},
				},
			);

			defaultLanguage = productForItem?.default_language || LANGUAGE_FALLBACK;

			break;

		case SCOPES.ORGANIZATION:

			// Fetch default language from the associated product (if any)
			if (associatedObjectType === ASSOCIATED_OBJECT_TYPES.PRODUCT && associatedObjectId) {

				const associatedProduct = await this.tcRequest.get(
					hostname,
					'products',
					associatedObjectId,
					{
						params: {
							organization_id: organizationId,
						},
					},
				);

				defaultLanguage = associatedProduct?.default_language || LANGUAGE_FALLBACK;

			// Otherwise, fetch default language from the organization
			} else {

				const organization = await this.tcRequest.get(
					hostname,
					'organizations',
					organizationId,
					{
						params: {
							organization_id: organizationId,
						},
					},
				);

				defaultLanguage = organization?.language || LANGUAGE_FALLBACK;

			}

			break;

		default:

			defaultLanguage = LANGUAGE_FALLBACK;

			break;

		}

		// Build the final list of languages to use for filtering
		const languages = [defaultLanguage];

		// Check if requesting an additional language, other than the default
		if (requestedLanguage &&
			requestedLanguage !== defaultLanguage &&
			Boolean(LANGUAGE_CODES[requestedLanguage])) {

			languages.push(requestedLanguage);

		}

		return {
			defaultLanguage,
			languages,
		};

	}

	/**
	 * Detects the language of a given text string.
	 *
	 * @param {String} text  The text to process.
	 * @param {Object} options  Options for language detection.
	 * @param {Boolean} options.isHTML  Flag indicating if the text is HTML.
	 *
	 * @returns {Object} result  The language detection result.
	 * @returns {String} result.language  The detected language of the text.
	 * @returns {Object} result.error  The API error, if any.
	 */
	async detectLanguage(text, options = {}) {

		let language, apiError;

		// Attempt to detect locally with cld library
		try {

			const result = await cld.detect(text, options);

			language = result.languages?.[0]?.code;

		} catch (cldError) {

			// Attempt to detect with Google Translate
			try {

				const results = await timeout(
					this.googleTranslate.detect(text),
					TIMEOUT_IN_MS
				);

				language = results?.[0]?.language;

				// Keep language undefined if Google returns
				// "undetermined" code
				if (language === LANGUAGE_UNDETERMINED) {
					language = undefined;
				}

			} catch (googleError) {

				apiError = googleError;
				console.error(googleError);

			}

		}

		return {
			language,
			error: Boolean(apiError),
		};

	}

	/**
	 * Detects the language of a given ProseMirror document object.
	 *
	 * @param {Object} doc  The ProseMirror document to process.
	 *
	 * @returns {Object} result
	 * @returns {String} result.language  The detected language.
	 * @returns {Bool}   result.error  Flag indicating if an error occurred.
	 */
	async detectProseMirrorLanguage(doc) {

		// Ignore undefined documents
		if (!doc) {
			return {};
		}

		const pmDoc = schema.nodeFromJSON(sanitizeProsemirror(doc));
		const text = pmDoc.textContent;

		// Ignore documents with no text content
		if (!text || text.length === 0) {
			return {};
		}

		return await this.detectLanguage(text, { isHTML: true });

	}

	/**
	 * Translates a given text to the provided target language with the DeepL API.
	 *
	 * @param {String} text  The text to be translated.
	 * @param {String} targetLanguage  The target language for the translation as an ISO-639-1 code.
	 * @param {Object} [options]  Additional translation options.
	 * @param {String} options.sourceLanguage  The source language of the original text as an
	 *                                         ISO-639-1 code.
	 * @param {String} options.isHTML  Flag indicating if text is formatted as an HTML string.
	 *
	 * @returns {Object} result
	 * @returns {String} result.translation  The translated text.
	 */
	async translateWithDeepL(text, targetLanguage, options = {}) {

		if (this.testMode) {
			return mockRequest({
				translation: 'This is translated text.',
			});
		}

		let deepLTarget = targetLanguage;

		if (targetLanguage === 'en') {
			deepLTarget = 'en-us';
		}

		if (targetLanguage === 'pt') {
			deepLTarget = 'pt-pt';
		}

		const result = await this.deepLTranslator.translateText(
			text,
			options.sourceLanguage,
			deepLTarget,
			{ tagHandling: options.isHTML ? 'html' : undefined }
		);

		return {
			translation: result.text,
		};

	}

	/**
	 * Translates a given text to the provided target language with the Google Translate API.
	 *
	 * @param {String} text  The text to be translated.
	 * @param {String} targetLanguage  The target language for the translation as an ISO-639-1 code.
	 * @param {Object} [options]  Additional translation options.
	 * @param {String} options.sourceLanguage  The source language of the original text as an
	 *                                         ISO-639-1 code.
	 * @param {String} options.isHTML  Flag indicating if text is formatted as an HTML string.
	 *
	 * @returns {Object} result
	 * @returns {String} result.translation  The translated text.
	 */
	async translateWithGoogle(text, targetLanguage, options = {}) {

		if (this.testMode) {
			return mockRequest({
				translation: 'This is translated text.',
			});
		}

		const [translation, response] = await this.googleTranslate.translate(text, {
			to: targetLanguage,
			from: options.sourceLanguage,
			format: options.isHTML ? 'html' : 'text',
		});

		const detectedSourceLanguage =
			response?.data?.translations?.[0]?.detectedSourceLanguage;

		return {
			translation,
			detectedSourceLanguage,
		};

	}

	/**
	 * Selects the appropriate translation provider given the source and target languages.
	 * If DeepL does not support both the source and target languages, we'll fall back to
	 * Google Translate.
	 *
	 * @param {String} sourceLanguage  The language of the original text as an ISO-639-1 code.
	 * @param {String} targetLanguage  The language of the translation as an ISO-639-1 code.
	 *
	 * @returns {Object}   result
	 * @returns {Function} result.request  Callback to make the translation API request.
	 * @returns {String}   result.provider  The name of the third-party translation service provider.
	 */
	selectTranslationProvider(sourceLanguage, targetLanguage) {

		if (!targetLanguage) {
			throw new Error('A target language must be provided.');
		}

		let provider, request;

		if (sourceLanguage &&
			DEEPL_SOURCE_LANGUAGES.includes(sourceLanguage) &&
			DEEPL_TARGET_LANGUAGES.includes(targetLanguage)) {

			provider = TRANSLATION_PROVIDERS.DEEPL;
			request = this.translateWithDeepL.bind(this);

		} else {

			provider = TRANSLATION_PROVIDERS.GOOGLE;
			request = this.translateWithGoogle.bind(this);

		}

		return {
			provider,
			request,
		};

	}

	/**
	 * Translates a provided text string into the target language.
	 *
	 * @param {String} text  The text to be translated.
	 * @param {String} targetLanguage  The target language for the translation as an ISO-639-1 code.
	 * @param {Object} [options]  Additional translation options.
	 * @param {String} options.sourceLanguage  The source language of the original text as a
	 *                                         ISO-639-1 code.
	 * @param {String} options.isHTML  Flag indicating if text is formatted as an HTML string.
	 *
	 * @returns {Object} result
	 * @returns {String} result.translation  The translated text.
	 * @returns {Bool}   result.error  Flag indicating if an error occurred when translating the text.
	 * @returns {String|undefined} result.provider  The third-party service used to perform the
	 *                                              translation.
	 */
	async translate(text, targetLanguage, options = {}) {

		if (options.sourceLanguage && targetLanguage === options.sourceLanguage) {
			return {
				translation: text,
				error: false,
				provider: undefined,
			};
		}

		const {
			provider,
			request,
		} = this.selectTranslationProvider(options.sourceLanguage, targetLanguage);

		let translation, translationError;

		try {

			if (this.timer) {
				console.time(`${provider}-${targetLanguage}`);
			}

			const result = await timeout(
				request(text, targetLanguage, options),
				TIMEOUT_IN_MS
			);

			if (this.timer) {
				console.timeEnd(`${provider}-${targetLanguage}`);
			}

			translation = result.translation;

		} catch (error) {

			console.error('Failed to translate text');
			console.error(error);

			translationError = true;

		}

		return {
			translation,
			error: Boolean(translationError),
			provider,
		};

	}

	/**
	 * Translates a given ProseMirror document object into the target language.
	 *
	 * @param {Object} doc  The ProseMirror document to be translated.
	 * @param {String} sourceLanguage  The source language of the original text as an ISO-639-1 code.
	 * @param {String} targetLanguage  The target language for the translation as an ISO-639-1 code.
	 *
	 * @returns {Object} result
	 * @returns {Object} result.translation  The translated ProseMirror document.
	 * @returns {Bool}   result.error  Flag indicating if an error occurred when translating the text.
	 * @returns {String} result.provider  The third-party service used to perform the translation.
	 */
	async translateProseMirror(doc, sourceLanguage, targetLanguage) {

		if (!doc) {
			throw new Error('A ProseMirror document must be provided');
		}

		if (!targetLanguage) {
			throw new Error('A target language must be provided');
		}

		const sanitizedDoc = sanitizeProsemirror(doc);
		const docNode = schema.nodeFromJSON(sanitizedDoc);
		const plainText = docNode.textContent;
		const htmlText = serializeProsemirror(sanitizedDoc);

		// Ignore documents with no text content
		if (!plainText || plainText.length === 0) {
			return {};
		}

		// Ignore empty HTML strings
		if (!htmlText || htmlText.length === 0) {
			return {};
		}

		const result = await this.translate(htmlText, targetLanguage, {
			isHTML: true,
			sourceLanguage,
		});

		let translatedPMContent;

		if (result.translation) {
			translatedPMContent = deserialize(result.translation);
		}

		return {
			...result,
			translation: translatedPMContent,
		};

	}

}

module.exports = TCIntl;
